import './styles/RoleGeneral.scss';

import { useQuery } from '@apollo/client';
import {
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  Paper,
  Switch,
  TextField,
  Autocomplete
} from '@mui/material';

import { Field, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ENTITLEMENTS_QUERY from 'gql/queries/PermissionProvider/getEntitlements.gql';

import GQLGuard from 'app/components/GQLGuard';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { roleIcons, RoleIconType } from 'utils/RoleIcons';

interface Entitlement {
  id: number;
  entitlement: string;
}

export function RoleGeneral() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const entitlementsQuery = useQuery<any, any>(ENTITLEMENTS_QUERY, {
    variables: { all: true },
    fetchPolicy: 'no-cache'
  });
  const { setFieldValue, setFieldTouched, values, errors, touched }: any = useFormikContext();

  return (
    <GQLGuard
      query={entitlementsQuery}
      onError={error => {
        dispatch(
          AlertActions.error(`${t(translations.pages.managePermissionsSettings.errorLoading())}`)
        );
        navigate('/', { replace: true });
        return null;
      }}
    >
      {data => (
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <FormControl className="role-detail-modal__body__input" required>
              <Field
                as={TextField}
                name="name"
                fullWidth
                label={t(translations.pages.managePermissionsSettings.roleName())}
                error={touched.name && errors.name}
                helperText={touched.name && errors.name}
                required
              />
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl className="role-detail-modal__body__input" required fullWidth>
              <FormControlLabel
                control={
                  <Autocomplete
                    options={roleIcons as RoleIconType[]}
                    value={values.icon && roleIcons.find(ent => ent.icon === values.icon)}
                    autoHighlight
                    fullWidth
                    getOptionLabel={option => option.icon}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <span
                          style={{
                            marginRight: '1em'
                          }}
                        >
                          <Icon className={option.icon + ' role-detail-modal__body__input__icon'} />
                        </span>
                        {option.icon}
                      </li>
                    )}
                    onChange={async (event, value, reason) => {
                      setFieldValue('icon', value?.icon, true);
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        error={touched.icon && errors.icon}
                        helperText={touched.icon && errors.icon}
                        fullWidth
                        required
                        onBlur={() => {
                          if (!touched.icon) {
                            setFieldTouched('icon', true);
                          }
                        }}
                        label={t(translations.pages.managePermissionsSettings.icon())}
                        inputProps={{
                          ...params.inputProps,
                          style: { padding: '6 0 7' },
                          autoComplete: 'new-password'
                        }}
                      />
                    )}
                  />
                }
                label={''}
                style={{ width: '100%', marginBottom: '0.5em' }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={4} className="role-detail-modal__body__input">
            <FormControl className="role-detail-modal__body__input" required fullWidth>
              <FormControlLabel
                control={
                  <Switch
                    checked={values.active}
                    onChange={v => {
                      setFieldValue('active', v.target.checked);
                    }}
                    name="withAppointment"
                    color="primary"
                  />
                }
                label={t(translations.pages.managePermissionsSettings.active()) as string}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={data.entitlements as Entitlement[]}
              value={
                values.groupEntitlements &&
                values.groupEntitlements.map(selectedEntitlement =>
                  data.entitlements.find(ent => {
                    return ent.id === selectedEntitlement;
                  })
                )
              }
              getOptionLabel={ent => ent.entitlement}
              onChange={(ent, value, reason) => {
                setFieldValue(
                  'groupEntitlements',
                  value.map(ent => ent.id)
                );
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  error={touched.groupEntitlements && errors.groupEntitlements}
                  helperText={touched.groupEntitlements && errors.groupEntitlements}
                  variant="outlined"
                  label={t(translations.pages.managePermissionsSettings.entitlements())}
                  margin="normal"
                  fullWidth
                  onBlur={() => {
                    if (!touched.groupEntitlements) {
                      setFieldTouched('groupEntitlements', true);
                    }
                  }}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password'
                  }}
                />
              )}
              renderTags={(value, getPropTags) => {
                return value.map((ent, index) => (
                  <Chip
                    component={Paper}
                    elevation={2}
                    {...getPropTags({ index: index })}
                    key={ent.id}
                    label={ent.entitlement}
                  />
                ));
              }}
            />
          </Grid>
        </Grid>
      )}
    </GQLGuard>
  );
}
